// src/WhySkillMatchAI.jsx (or your components directory)
import React from "react";

// Import your images (assuming these are correct from previous step)
import careerIconAsset from "../../assets/images/landing/catIcon1.png"; // Or .svg, .jpg, etc.
import tailoringIconAsset from "../../assets/images/landing/catIcon2.png";
import matchingIconAsset from "../../assets/images/landing/catIcon3.png";
import optimizeIconAsset from "../../assets/images/landing/catIcon4.png";

// STEP 1: Simplify PlaceholderIcon component
const PlaceholderIcon = ({ imgSrc, altText }) => (
  // The component now directly returns an img tag
  // Applied sizing (w-14 h-14 for 56px, use w-16 h-16 for 64px)
  // Added object-contain to preserve aspect ratio
  // Added mb-4 for spacing, which was previously on the container div
  <img
    src={imgSrc}
    alt={altText || "Benefit Icon"}
    className="w-14 h-14 object-contain mb-4" // For 56px. Use w-16 h-16 for 64px.
  />
);

// STEP 2: Update BenefitCard component (remove bgColor prop)
const BenefitCard = ({ imgSrc, title, description }) => (
  <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg h-full flex flex-col">
    <PlaceholderIcon
      imgSrc={imgSrc}
      altText={title}
      // bgColor prop removed
    />
    <h3
      className="text-lg md:text-xl font-semibold mb-2"
      style={{
        color: "#073214",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {title}
    </h3>
    <p
      className="text-gray-600 text-sm md:text-base leading-relaxed"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      {description}
    </p>
  </div>
);

const WhySkillMatchAI = () => {
  // STEP 3: Update benefits array (remove bgColor property)
  const benefits = [
    {
      imgSrc: careerIconAsset,
      title: "AI Career Mapping",
      description:
        "Go beyond job titles. SkillMatch identifies your core strengths and suggests career directions that match your unique profile.",
      // bgColor: "bg-red-100", // Removed
    },
    {
      imgSrc: tailoringIconAsset,
      title: "Resume Tailoring to Job Descriptions",
      description:
        "Each job is unique—your Resume should be too. Our tool rewrites and optimizes your Resume to pass ATS filters and impress recruiters.",
      // bgColor: "bg-blue-100", // Removed
    },
    {
      imgSrc: matchingIconAsset,
      title: "Smart Job Matching",
      description:
        "Don't waste time applying randomly. Our AI suggests jobs that align perfectly with your skillset, goals, and experience.",
      // bgColor: "bg-yellow-100", // Removed
    },
    {
      imgSrc: optimizeIconAsset,
      title: "Build & Optimize a Resume in Minutes",
      description:
        "Use our intelligent builder to create a resume that's professional, personalized, and powerful—optimized for every opportunity.",
      // bgColor: "bg-sky-100", // Removed
    },
  ];

  return (
    <section
      className="text-white py-16 lg:py-24"
      style={{
        backgroundColor: "#073214",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-12 lg:mb-16">
          <div className="lg:flex lg:justify-between lg:items-center">
            <div className="text-center lg:text-left">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Why SkillMatch AI?
              </h2>
              <p
                className="text-lg text-gray-300 max-w-xl mx-auto lg:mx-0"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                SkillMatch AI uses advanced profiling and smart tools to match
                jobs, optimize Resumes, and fast-track your path to success.
              </p>
            </div>
            <div className="mt-6 lg:mt-0 text-center lg:text-right flex-shrink-0">
              <a
                href="#pricing"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-[#FFFFFF] bg-[#E55812] hover:bg-[#D0500E] transition-colors"
                style={{
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                See Pricing
                <svg
                  className="w-5 h-5 ml-2 -mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  ></path>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              imgSrc={benefit.imgSrc}
              title={benefit.title}
              description={benefit.description}
              // bgColor prop removed from here too
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhySkillMatchAI;
